import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Space, Radio, Flex } from 'antd';
import {
  AppstoreAddOutlined,
  FileExcelOutlined,
  PauseCircleOutlined,
  PlayCircleOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { IconButton } from '../../../../common/controls/Controls';
import { useDataGridTextFilter } from '../../../../common/DataGrid/filters/useDataGridTextFilter';
import {
  dataGridNumberSorter,
  dataGridTextSorter,
} from '../../../../common/DataGrid/sorters/DataGridSorters';
import DataGrid from '../../../../common/DataGrid/DataGrid';
import {
  CollapseBlock,
  Spinner,
  QuestionModal,
} from '../../../../common/index';
import useIsUserAdmin from '../../../../../hooks/useIsUserAdmin';
import useIsUserErasmusKoordinator from '../../../../../hooks/useIsUserErasmusKoordinator';
import { useToastMessages } from '../../../../../hooks/useToastMessages';
import {
  useGenerateErasmusUniversitiesExcelMutation,
  useSaveErasmusUniversityMutation,
} from '../../../../../redux/services/erasmusUniversities';

const UNIVERSITY_UPDATE_FIELDS = [
  'name',
  'country',
  'city',
  'OID',
  'erasmus_code',
  'PIC',
  'contact_person',
  'email',
  'phone',
  'description',
  'website',
  'latitude',
  'longitude',
  'title_photo_path',
];

const ErasmusUniversitiesTable = ({
  erasmusUniversities,
  onDelete,
  onDetailClick,
  onPreviewClick,
  onDataChanged,
  showFilterBlock = true,
}) => {
  const { t } = useTranslation();
  const { apiErrorMessage, warningMessage, successMessage } =
    useToastMessages();
  const isAdmin = useIsUserAdmin();
  const isErasmusKoordinator = useIsUserErasmusKoordinator();
  const hasErasmusElevatedAccess = isAdmin || isErasmusKoordinator;
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedOption, setSelectedOption] = useState('active');
  const [bulkActionModalVisible, setBulkActionModalVisible] = useState(false);
  const [
    generateErasmusUniversitiesExcel,
    { isLoading: isExcelExportLoading },
  ] = useGenerateErasmusUniversitiesExcelMutation();
  const [saveErasmusUniversity, { isLoading: isBulkUpdateLoading }] =
    useSaveErasmusUniversityMutation();

  useEffect(() => {
    if (!hasErasmusElevatedAccess) {
      setSelectionMode(false);
      setSelectedRowKeys([]);
    }
  }, [hasErasmusElevatedAccess]);

  useEffect(() => {
    setSelectedRowKeys([]);
  }, [selectedOption]);

  const toggleSelectionMode = () => {
    setSelectionMode((prev) => {
      const next = !prev;
      if (!next) {
        setSelectedRowKeys([]);
      }
      return next;
    });
  };
  //const IDFilterProps = useDataGridTextFilter('id');
  const NameFilterProps = useDataGridTextFilter('name');
  const CountryFilterProps = useDataGridTextFilter('country');
  const CityFilterProps = useDataGridTextFilter('city');
  const OIDFilterProps = useDataGridTextFilter('OID');
  const ErasmusCodeFilterProps = useDataGridTextFilter('erasmus_code');
  const PICFilterProps = useDataGridTextFilter('PIC');

  const columns = useMemo(
    () => [
      {
        title: t('Mesto'),
        dataIndex: 'city',
        width: 120,
        ellipsis: true,
        sorter: dataGridNumberSorter('city'),
        ...CityFilterProps,
      },
      {
        title: 'Názov',
        dataIndex: 'name',
        width: 350,
        ellipsis: true,
        sorter: dataGridTextSorter('name'),
        defaultSortOrder: 'ascend',
        ...NameFilterProps,
      },
      {
        title: 'Krajina',
        dataIndex: 'country',
        width: 150,
        ellipsis: true,
        sorter: dataGridTextSorter('country'),
        defaultSortOrder: 'ascend',
        ...CountryFilterProps,
      },
      ...(hasErasmusElevatedAccess
        ? [
            {
              title: 'OID',
              dataIndex: 'OID',
              width: 120,
              ellipsis: true,
              sorter: dataGridTextSorter('OID'),
              defaultSortOrder: 'ascend',
              ...OIDFilterProps,
            },
          ]
        : []),
      {
        title: 'Erasmus kód',
        dataIndex: 'erasmus_code',
        width: 120,
        ellipsis: true,
        sorter: dataGridTextSorter('erasmus_code'),
        defaultSortOrder: 'ascend',
        ...ErasmusCodeFilterProps,
      },
      ...(hasErasmusElevatedAccess
        ? [
            {
              title: 'PIC',
              dataIndex: 'PIC',
              width: 120,
              ellipsis: true,
              sorter: dataGridTextSorter('PIC'),
              defaultSortOrder: 'ascend',
              ...PICFilterProps,
            },
          ]
        : []),
    ],
    [
      t,
      hasErasmusElevatedAccess,
      CityFilterProps,
      NameFilterProps,
      CountryFilterProps,
      OIDFilterProps,
      ErasmusCodeFilterProps,
      PICFilterProps,
    ],
  );

  if (!erasmusUniversities) {
    return <Spinner />;
  }

  const filteredUniversities = useMemo(() => {
    const showActive = selectedOption === 'active';
    return (erasmusUniversities || []).filter((university) =>
      showActive ? !!university.active : !university.active,
    );
  }, [erasmusUniversities, selectedOption]);

  const rowSelection =
    hasErasmusElevatedAccess && selectionMode
      ? {
          checkStrictly: true,
          selectedRowKeys,
          onChange: (newSelectedRowKeys) => {
            setSelectedRowKeys(newSelectedRowKeys);
          },
        }
      : undefined;

  const selectedUniversities = useMemo(() => {
    const selectedKeysSet = new Set(selectedRowKeys.map(String));
    return (filteredUniversities || []).filter((university) =>
      selectedKeysSet.has(String(university.id)),
    );
  }, [filteredUniversities, selectedRowKeys]);

  const handleExcelExport = async () => {
    if (selectedUniversities.length === 0) {
      warningMessage({
        message: t('Vyberte aspoň jednu univerzitu na export.'),
      });
      return;
    }

    try {
      const response = await generateErasmusUniversitiesExcel({
        body: selectedUniversities,
      }).unwrap();

      const byteCharacters = atob(response.excel);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Erasmus_universities.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  const handleBulkActionRequest = () => {
    if (selectedUniversities.length === 0) {
      warningMessage({ message: t('Vyberte aspoň jednu univerzitu.') });
      return;
    }
    setBulkActionModalVisible(true);
  };

  const handleBulkActionConfirm = async () => {
    try {
      const nextActiveState = selectedOption !== 'active';

      await Promise.all(
        selectedUniversities.map((university) => {
          const body = UNIVERSITY_UPDATE_FIELDS.reduce((acc, field) => {
            if (university[field] !== undefined) {
              acc[field] = university[field];
            }

            return acc;
          }, {});

          return saveErasmusUniversity({
            id: university.id,
            body: {
              ...body,
              active: nextActiveState,
            },
          }).unwrap();
        }),
      );

      successMessage({
        message:
          selectedOption === 'active'
            ? t('Vybrané univerzity boli deaktivované.')
            : t('Vybrané univerzity boli aktivované.'),
      });

      setBulkActionModalVisible(false);
      setSelectedRowKeys([]);
      onDataChanged && onDataChanged();
    } catch (error) {
      apiErrorMessage(error);
    }
  };

  return (
    <>
      {showFilterBlock && (
        <CollapseBlock collapsible={true} title={t('Filter univerzít')}>
          <Flex
            style={{ alignItems: 'center', justifyContent: 'space-between' }}
            gap="middle"
          >
            <Radio.Group
              block
              value={selectedOption}
              optionType="button"
              onChange={(e) => setSelectedOption(e.target.value)}
              style={{ whiteSpace: 'nowrap' }}
            >
              <Radio value="active">{t('Aktívne')}</Radio>
              <Radio value="inactive">{t('Neaktívne')}</Radio>
            </Radio.Group>

            {hasErasmusElevatedAccess ? (
              !selectionMode ? (
                <IconButton
                  icon={<AppstoreAddOutlined />}
                  tooltip={t('Hromadné akcie')}
                  onClick={toggleSelectionMode}
                />
              ) : (
                <Space>
                  <IconButton
                    icon={<FileExcelOutlined />}
                    tooltip={t('Exportovať do Excelu')}
                    loading={isExcelExportLoading}
                    disabled={selectedRowKeys.length === 0}
                    disabledTooltip={t('Vyberte aspoň jednu univerzitu')}
                    onClick={handleExcelExport}
                  />
                  <IconButton
                    icon={
                      selectedOption === 'active' ? (
                        <PauseCircleOutlined />
                      ) : (
                        <PlayCircleOutlined />
                      )
                    }
                    tooltip={
                      selectedOption === 'active'
                        ? t('Deaktivovať vybrané')
                        : t('Aktivovať vybrané')
                    }
                    loading={isBulkUpdateLoading}
                    disabled={selectedRowKeys.length === 0}
                    disabledTooltip={t('Vyberte aspoň jednu univerzitu')}
                    onClick={handleBulkActionRequest}
                  />
                  <IconButton
                    icon={<CloseOutlined />}
                    tooltip={t('Zatvoriť hromadné akcie')}
                    onClick={toggleSelectionMode}
                  />
                </Space>
              )
            ) : (
              <div />
            )}
          </Flex>
        </CollapseBlock>
      )}

      <DataGrid
        rowKey={(university) => university.id}
        columns={columns}
        dataSource={filteredUniversities}
        permissionsBasic={'erasmus-universities'}
        onEditClick={onDetailClick}
        onRowClick={
          hasErasmusElevatedAccess && selectionMode ? undefined : onDetailClick
        }
        onDelete={onDelete}
        onPreviewClick={onPreviewClick}
        rowClickOpensPreview={!selectionMode}
        rowSelection={rowSelection}
      />

      <QuestionModal
        chosenPositive={handleBulkActionConfirm}
        question={
          <div>
            <p style={{ marginBottom: 8 }}>
              {selectedOption === 'active'
                ? t('Naozaj chcete deaktivovať vybrané univerzity?')
                : t('Naozaj chcete aktivovať vybrané univerzity?')}
            </p>
            <div style={{ maxHeight: 220, overflowY: 'auto' }}>
              {selectedUniversities.map((university) => (
                <div key={university.id}>{university.name}</div>
              ))}
            </div>
          </div>
        }
        visible={bulkActionModalVisible}
        yes={selectedOption === 'active' ? t('Deaktivovať') : t('Aktivovať')}
        toggle={() => setBulkActionModalVisible(false)}
        headerTitle={
          selectedOption === 'active'
            ? t('Deaktivovať univerzity')
            : t('Aktivovať univerzity')
        }
      />
    </>
  );
};

ErasmusUniversitiesTable.propTypes = {
  erasmusUniversities: PropTypes.array,
  onDelete: PropTypes.func,
  onDetailClick: PropTypes.func,
  onCreateClick: PropTypes.func,
  onDataChanged: PropTypes.func,
  showFilterBlock: PropTypes.bool,
};

export default ErasmusUniversitiesTable;
